import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { toCurrency, toPercent, toCurrencyWholeNumber } from "@helpers";
import { InvestmentsChartDataStatistic } from "@api/v1/hooks/performance/useInvestmentsChartData";
import { GainLossTypography } from "@rentablo/components";
import {
  InvestmentsTableContainer,
  InvestmentsTableSummaryWrapper,
  GainLossTypographyBox,
  GainLossPercentsStyled,
  SummaryItem,
} from "./components";
import { InvestmentsTableSummaryMobile } from "./InvestmentsTableSummaryMobile";

export interface Props {
  statistic: InvestmentsChartDataStatistic;
  currency?: string;
  // Phones only: the market value alone, see InvestmentsTableSummaryMobile.
  valueOnly?: boolean;
}

export const InvestmentsTableSummary = ({
  statistic,
  currency = "EUR",
  valueOnly = false,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down("md"));

  return (
    // @ts-ignore
    <InvestmentsTableContainer>
      <InvestmentsTableSummaryWrapper>
        {isMd ? (
          <InvestmentsTableSummaryMobile
            statistic={statistic}
            currency={currency}
            valueOnly={valueOnly}
          />
        ) : (
          <>
            <Box display="flex" flexDirection="row" gap={10}>
              <SummaryItem label={t("investmentsTable.summary.today")}>
                <Box display="flex">
                  <GainLossTypography
                    variant="subtitle2"
                    kpmg-germany={{ variant: "body2" }}
                    value={statistic.changeTodayAbs}
                  >
                    {toCurrency(statistic.changeTodayAbs, currency)}
                  </GainLossTypography>
                  &nbsp;
                  <GainLossTypography
                    variant="subtitle2"
                    kpmg-germany={{ variant: "body2" }}
                    value={statistic.changeToday}
                  >
                    ({toPercent(statistic.changeToday)})
                  </GainLossTypography>
                </Box>
              </SummaryItem>
              <SummaryItem label={t("investmentsTable.summary.purchaseValue")}>
                <GainLossTypography
                  variant="subtitle2"
                  kpmg-germany={{ variant: "body2" }}
                  value={statistic.investedCapital}
                >
                  {toCurrency(statistic.investedCapital, currency)}
                </GainLossTypography>
              </SummaryItem>
              <SummaryItem label={t("investmentsTable.summary.priceGains")}>
                <GainLossTypography
                  variant="subtitle2"
                  kpmg-germany={{ variant: "body2" }}
                  value={statistic.valuationGain}
                >
                  {toCurrency(statistic.valuationGain, currency)}
                </GainLossTypography>
              </SummaryItem>
            </Box>
            <Box>
              <GainLossTypographyBox>
                <Typography variant="h4" noWrap>
                  {toCurrencyWholeNumber(statistic.portfolioValue, currency)}
                </Typography>
                <GainLossPercentsStyled
                  value={statistic.valuationGainPercent ?? 0}
                />
              </GainLossTypographyBox>
            </Box>
          </>
        )}
      </InvestmentsTableSummaryWrapper>
    </InvestmentsTableContainer>
  );
};
